import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/base/Button';
import { sendDonationReceiptEmail } from '../../../services/emailService';

// Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

export default function IndividualGivingSection() {
  const navigate = useNavigate();
  const [selectedAmount, setSelectedAmount] = useState(10000);
  const [customAmount, setCustomAmount] = useState('');
  const [isMonthly, setIsMonthly] = useState(false);
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [donorInfo, setDonorInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    panNumber: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const presetAmounts = [5000, 10000, 25000, 50000];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(0);
  };

  const handleDonateClick = () => {
    setShowDonationForm(true);
  };

  const handleInputChange = (field: string, value: string) => {
    setDonorInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const donationAmount = customAmount ? parseInt(customAmount) : selectedAmount;
    
    try {
      const options = {
        key: 'rzp_live_SLW5EZjmEu3Hpf',
        amount: donationAmount * 100,
        currency: 'INR',
        name: 'Diatomic Life Foundation',
        description: `${isMonthly ? 'Monthly' : 'One-time'} Donation`,
        image: 'https://public.readdy.ai/ai/img_res/ddf9244497b7103272b6ef9700a82aba.jpg',
        prefill: {
          name: donorInfo.name,
          email: donorInfo.email,
          contact: donorInfo.phone
        },
        notes: {
          address: donorInfo.address,
          panNumber: donorInfo.panNumber,
          isMonthly: isMonthly.toString()
        },
        theme: {
          color: '#12B6B6'
        },
        handler: async function (response: any) {
          // Submit form data to Readdy form
          const formData = new URLSearchParams();
          formData.append('name', donorInfo.name);
          formData.append('email', donorInfo.email);
          formData.append('phone', donorInfo.phone);
          formData.append('address', donorInfo.address);
          formData.append('panNumber', donorInfo.panNumber);
          formData.append('amount', donationAmount.toString());
          formData.append('isMonthly', isMonthly.toString());
          formData.append('currency', 'INR');
          formData.append('paymentId', response.razorpay_payment_id);
          formData.append('paymentStatus', 'Success');

          await fetch('https://readdy.ai/api/form/d31rreo5vkms9brjvaj0', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString()
          });

          // Send receipt email to donor
          const receiptNumber = `DLF-${Date.now().toString().slice(-8)}`;
          const donationDate = new Date().toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          });

          await sendDonationReceiptEmail({
            donor_name: donorInfo.name,
            donor_email: donorInfo.email,
            donation_amount: `₹${donationAmount.toLocaleString('en-IN')}`,
            payment_id: response.razorpay_payment_id,
            payment_type: isMonthly ? 'Monthly Recurring' : 'One-time Donation',
            donation_date: donationDate,
            receipt_number: receiptNumber,
            pan_number: donorInfo.panNumber,
          });

          setIsSubmitting(false);

          // Redirect to Thank You page with donation summary
          const params = new URLSearchParams({
            name: donorInfo.name,
            email: donorInfo.email,
            amount: donationAmount.toString(),
            paymentId: response.razorpay_payment_id,
            isMonthly: isMonthly.toString(),
            receiptNumber,
          });
          navigate(`/thank-you?${params.toString()}`);
        },
        modal: {
          ondismiss: function() {
            setIsSubmitting(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment initialization error:', error);
      setSubmitMessage('There was an error processing your donation. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-black mb-8">
            Support a Family Today.
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Every ₹10,000 provides app access, OxyKits (capsules + sprays), and oxygen 
            education for 2 families for a full year.
          </p>
        </div>

        {/* Donation Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm">
            {/* Amount Selection */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-black mb-6">Choose Amount</h3>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {presetAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handleAmountSelect(amount)}
                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                      selectedAmount === amount
                        ? 'border-[#12B6B6] bg-[#12B6B6]/5 text-[#12B6B6]'
                        : 'border-gray-200 hover:border-gray-300 text-black'
                    }`}
                  >
                    <div className="font-semibold">₹{amount.toLocaleString()}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      {amount === 5000 ? 'Support 1 family' : 
                       amount === 10000 ? 'Support 2 families' :
                       amount === 25000 ? 'Support 5 families' :
                       'Support 10 families'}
                    </div>
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="relative">
                <input
                  type="number"
                  placeholder="Enter custom amount"
                  value={customAmount}
                  onChange={(e) => handleCustomAmount(e.target.value)}
                  className="w-full p-4 border-2 border-gray-200 rounded-2xl text-black placeholder-gray-400 focus:border-[#12B6B6] focus:outline-none text-lg pl-8"
                />
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg">₹</span>
              </div>
            </div>

            {/* Monthly Toggle */}
            <div className="mb-8">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                <div>
                  <div className="font-medium text-black">Monthly Giving</div>
                  <div className="text-sm text-gray-600">Sustain ongoing impact</div>
                </div>
                <button
                  onClick={() => setIsMonthly(!isMonthly)}
                  className={`w-12 h-6 rounded-full transition-all cursor-pointer ${
                    isMonthly ? 'bg-[#12B6B6]' : 'bg-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-all ${
                    isMonthly ? 'translate-x-6' : 'translate-x-0.5'
                  }`}></div>
                </button>
              </div>
            </div>

            {/* Donate Button */}
            <Button 
              variant="primary" 
              size="lg" 
              className="w-full text-lg py-4 mb-6"
              onClick={handleDonateClick}
            >
              Donate ₹{(customAmount ? parseInt(customAmount) : selectedAmount).toLocaleString()}
              {isMonthly ? '/month' : ''}
            </Button>

            {/* Payment Info */}
            <div className="text-center space-y-2">
              <div className="text-sm text-gray-600">
                Instant 80G receipt via email. UPI, Card, Netbanking supported.
              </div>
              <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                <div className="flex items-center space-x-1">
                  <i className="ri-shield-check-line"></i>
                  <span>Secure</span>
                </div>
                <div className="flex items-center space-x-1">
                  <i className="ri-time-line"></i>
                  <span>Instant Receipt</span>
                </div>
                <div className="flex items-center space-x-1">
                  <i className="ri-file-text-line"></i>
                  <span>80G Eligible</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Donation Form Modal */}
      {showDonationForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-black">Complete Your Donation</h3>
                <button
                  onClick={() => setShowDonationForm(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="mb-6 p-4 bg-[#12B6B6]/10 rounded-2xl">
                <div className="text-lg font-semibold text-[#12B6B6]">
                  ₹{(customAmount ? parseInt(customAmount) : selectedAmount).toLocaleString()}
                  {isMonthly ? '/month' : ''}
                </div>
                <div className="text-sm text-gray-600">
                  {isMonthly ? 'Monthly donation' : 'One-time donation'}
                </div>
              </div>

              {submitMessage && (
                <div className={`mb-6 p-4 rounded-2xl ${
                  submitMessage.includes('Thank you') 
                    ? 'bg-green-50 text-green-800' 
                    : 'bg-red-50 text-red-800'
                }`}>
                  {submitMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form id="donation-form">
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={donorInfo.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-xl text-black focus:border-[#12B6B6] focus:outline-none"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={donorInfo.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-xl text-black focus:border-[#12B6B6] focus:outline-none"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={donorInfo.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-xl text-black focus:border-[#12B6B6] focus:outline-none"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Address *
                    </label>
                    <textarea
                      name="address"
                      required
                      maxLength={500}
                      value={donorInfo.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-xl text-black focus:border-[#12B6B6] focus:outline-none h-20 resize-none"
                      placeholder="Enter your complete address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      PAN Number (for 80G receipt)
                    </label>
                    <input
                      type="text"
                      name="panNumber"
                      value={donorInfo.panNumber}
                      onChange={(e) => handleInputChange('panNumber', e.target.value.toUpperCase())}
                      className="w-full p-3 border border-gray-200 rounded-xl text-black focus:border-[#12B6B6] focus:outline-none"
                      placeholder="Enter your PAN number"
                      maxLength={10}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full text-lg py-4"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Processing...
                    </>
                  ) : (
                    `Proceed to Payment - ₹${(customAmount ? parseInt(customAmount) : selectedAmount).toLocaleString()}`
                  )}
                </Button>

                <div className="text-xs text-gray-500 text-center mt-4">
                  Your donation is secure and you will receive an 80G tax receipt via email
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
